import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { 
  AlertTriangle, Trash2, DollarSign, Rocket, CheckCircle, 
  XCircle, Users, RefreshCw, Shield, Database, Car
} from 'lucide-react';
import { toast } from 'sonner';

const LaunchPreparation = ({ api }) => {
  const [status, setStatus] = useState(null);
  const [verification, setVerification] = useState(null);
  const [testVehicles, setTestVehicles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    fetchStatus();
    fetchTestVehicles();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/platform-owner/launch-prep/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch launch prep status:', error);
      toast.error('Failed to load launch preparation status');
    } finally {
      setLoading(false);
    }
  };

  const fetchTestVehicles = async () => {
    try {
      const response = await api.get('/platform-owner/launch-prep/test-vehicles-preview');
      setTestVehicles(response.data);
    } catch (error) {
      console.error('Failed to fetch test vehicles:', error);
    }
  };

  const fetchVerification = async () => {
    try {
      const response = await api.get('/platform-owner/launch-prep/verify');
      setVerification(response.data);
    } catch (error) {
      console.error('Failed to fetch verification:', error);
    }
  };

  const openConfirmModal = (action) => {
    setConfirmAction(action);
    setConfirmText('');
    setShowConfirmModal(true);
  };

  const executeAction = async () => {
    // Confirmation text varies by action.
    let requiredText;
    if (confirmAction === 'delete-vehicles') {
      requiredText = 'DELETE TEST VEHICLES';
    } else if (confirmAction === 'archive-vehicles') {
      requiredText = 'ARCHIVE TEST VEHICLES';
    } else {
      requiredText = 'LAUNCH CLEANUP';
    }

    if (confirmText !== requiredText) {
      toast.error(`Please type ${requiredText} to confirm`);
      return;
    }

    setActionLoading(true);
    try {
      let endpoint = '';
      let successMessage = '';
      let body = {};
      
      switch (confirmAction) {
        case 'delete-users':
          endpoint = '/platform-owner/launch-prep/delete-test-users';
          successMessage = 'Test users deleted successfully';
          break;
        case 'reset-financial':
          endpoint = '/platform-owner/launch-prep/reset-test-financial';
          successMessage = 'Test financial data marked successfully';
          break;
        case 'set-live':
          endpoint = '/platform-owner/launch-prep/set-live';
          successMessage = 'Platform marked as LIVE!';
          break;
        case 'delete-vehicles':
          endpoint = '/platform-owner/launch-prep/delete-test-vehicles';
          successMessage = 'Test vehicles deleted successfully';
          body = { confirm: 'DELETE TEST VEHICLES' };
          break;
        case 'archive-vehicles':
          endpoint = '/platform-owner/launch-prep/archive-test-vehicles';
          successMessage = 'Test vehicles archived (audit-safe)';
          break;
        default:
          return;
      }

      const response = await api.post(endpoint, body);
      
      if (response.data.success) {
        toast.success(successMessage);
        setShowConfirmModal(false);
        setConfirmText('');
        
        // Refresh status and verification
        await fetchStatus();
        await fetchVerification();
        await fetchTestVehicles();
      } else {
        toast.error(response.data.error || 'Action failed');
      }
    } catch (error) {
      console.error('Action failed:', error);
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getActionTitle = () => {
    switch (confirmAction) {
      case 'delete-users':
        return 'Delete Test Users';
      case 'reset-financial':
        return 'Reset Test Financial Data';
      case 'set-live':
        return 'Mark Platform as LIVE';
      case 'delete-vehicles':
        return 'Delete Test Vehicles';
      case 'archive-vehicles':
        return 'Archive Test Vehicles (Safe)';
      default:
        return 'Confirm Action';
    }
  };

  const getActionDescription = () => {
    switch (confirmAction) {
      case 'delete-users':
        return 'This will permanently delete test user accounts and anonymize their financial records. Platform owners and users with real bookings are protected.';
      case 'reset-financial':
        return 'This will mark all test financial records (Stripe test mode, test emails) as test_data=true. They will be excluded from dashboard totals but preserved for audit purposes.';
      case 'set-live':
        return 'This will mark the platform as LIVE. All future bookings will be treated as real production data. This action cannot be undone.';
      case 'delete-vehicles':
        return 'This will permanently delete all test vehicles (Test/E2E/Do Not Book naming, or test_data flag). Vehicles with active bookings are protected.';
      case 'archive-vehicles':
        return 'Marks all detected test vehicles as test_data=true so they drop out of public listings — fully reversible by clearing the flag in the database. Real listings (Ssangyong Musso, Our Kaapi Story Coffee Trailer, etc.) are not touched.';
      default:
        return '';
    }
  };

  const getConfirmationText = () => {
    if (confirmAction === 'delete-vehicles') return 'DELETE TEST VEHICLES';
    if (confirmAction === 'archive-vehicles') return 'ARCHIVE TEST VEHICLES';
    return 'LAUNCH CLEANUP';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <Card className="bg-gradient-to-r from-red-900/30 to-orange-900/30 border-red-500/50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-bold text-red-400 mb-2">Pre-Launch Cleanup Tools</h3>
              <p className="text-white/80">
                These tools are <strong>irreversible</strong> and intended for launch preparation only. 
                All actions are logged for audit purposes. Use with caution.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Platform Status */}
      <Card className="bg-black/40 border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-400" />
            Platform Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {status?.platform_status?.is_live ? (
              <>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 px-4 py-2">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  LIVE
                </Badge>
                <span className="text-white/60">
                  Since: {new Date(status.platform_status.live_since).toLocaleString()}
                </span>
              </>
            ) : (
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 px-4 py-2">
                <AlertTriangle className="w-4 h-4 mr-2" />
                PRE-LAUNCH / TESTING
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cleanup Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Test Users */}
        <Card className="bg-black/40 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-400" />
              Test Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white mb-2">
              {status?.test_users?.deletable_count || 0}
            </div>
            <p className="text-xs text-white/60">
              {status?.test_users?.protected_count || 0} protected (real bookings)
            </p>
          </CardContent>
        </Card>

        {/* Test Financial Records */}
        <Card className="bg-black/40 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Database className="w-4 h-4 text-orange-400" />
              Test Records to Mark
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white mb-2" data-testid="active-test-records-count">
              {status?.cleanup_preview?.records_to_mark_test || 0}
            </div>
            <p className="text-xs text-white/60" data-testid="active-test-bookings-count">
              {status?.test_financial_data?.test_bookings_count || 0} active test bookings
            </p>
            {(status?.test_financial_data?.test_bookings_archived || 0) > 0 && (
              <p
                className="text-[10px] text-white/40 mt-1 italic"
                data-testid="archived-test-bookings-count"
                title="Already marked test_data=true — preserved for audit, excluded from financial totals"
              >
                + {status.test_financial_data.test_bookings_archived} archived (audit-only)
              </p>
            )}
          </CardContent>
        </Card>

        {/* Current vs Real Earnings */}
        <Card className="bg-black/40 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-400" />
              Earnings After Cleanup
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-400 mb-2">
              ${status?.cleanup_preview?.earnings_after_cleanup_dollars?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-white/60">
              Current: ${status?.test_financial_data?.current_totals?.total_earnings_dollars?.toFixed(2) || '0.00'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 1. Delete Test Users */}
        <Card className="bg-black/40 border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Delete Test Users
            </CardTitle>
            <CardDescription>
              Safely remove test accounts while preserving audit history
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-white/70 space-y-2">
              <p><strong>Will delete users where:</strong></p>
              <ul className="list-disc list-inside text-xs space-y-1 text-white/60">
                <li>Email contains "test" or "example"</li>
                <li>Email ends with @hirehub.test</li>
                <li>Role = auto_test</li>
                <li>created_by_seed = true</li>
              </ul>
              <p className="text-green-400 text-xs mt-2">
                <CheckCircle className="w-3 h-3 inline mr-1" />
                Platform owners and users with real bookings are protected
              </p>
            </div>
            <Button 
              onClick={() => openConfirmModal('delete-users')}
              variant="destructive"
              className="w-full"
              disabled={status?.test_users?.deletable_count === 0}
              data-testid="delete-test-users-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {status?.test_users?.deletable_count || 0} Test Users
            </Button>
          </CardContent>
        </Card>

        {/* 2. Reset Financial Data */}
        <Card className="bg-black/40 border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-400">
              <DollarSign className="w-5 h-5" />
              Reset Test Financial Data
            </CardTitle>
            <CardDescription>
              Mark test records to exclude from dashboard totals
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-white/70 space-y-2">
              <p><strong>Will mark as test_data=true:</strong></p>
              <ul className="list-disc list-inside text-xs space-y-1 text-white/60">
                <li>Stripe test mode payments</li>
                <li>Records with test/example emails</li>
                <li>Pre-launch bookings (if live flag set)</li>
              </ul>
              <p className="text-blue-400 text-xs mt-2">
                <Database className="w-3 h-3 inline mr-1" />
                Data preserved for audit - only excluded from totals
              </p>
            </div>
            <Button 
              onClick={() => openConfirmModal('reset-financial')}
              variant="outline"
              className="w-full border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
              data-testid="reset-financial-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Mark Test Financial Records
            </Button>
          </CardContent>
        </Card>

        {/* 3. Set Live Flag */}
        <Card className="bg-black/40 border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-400">
              <Rocket className="w-5 h-5" />
              Mark Platform as LIVE
            </CardTitle>
            <CardDescription>
              Set the launch timestamp for production mode
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-white/70 space-y-2">
              <p><strong>This action:</strong></p>
              <ul className="list-disc list-inside text-xs space-y-1 text-white/60">
                <li>Creates platform_live_at timestamp</li>
                <li>Future bookings NOT marked as test</li>
                <li>Enables production mode tracking</li>
              </ul>
              <p className="text-yellow-400 text-xs mt-2">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Run cleanup steps BEFORE marking live
              </p>
            </div>
            <Button 
              onClick={() => openConfirmModal('set-live')}
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={status?.platform_status?.is_live}
              data-testid="set-live-btn"
            >
              <Rocket className="w-4 h-4 mr-2" />
              {status?.platform_status?.is_live ? 'Already LIVE' : 'Mark as LIVE'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Test Vehicles Cleanup — Audit-Safe Archive (preferred) + Hard Delete (opt-in) */}
      <Card className="bg-black/40 border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-purple-400">
            <Car className="w-5 h-5" />
            Test Vehicle Cleanup
          </CardTitle>
          <CardDescription>
            Detect and remove test/E2E vehicles from public listings. Real
            host listings are excluded automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-white/70 space-y-2">
            <p><strong>Will detect vehicles where:</strong></p>
            <ul className="list-disc list-inside text-xs space-y-1 text-white/60">
              <li>Make starts with <code>TEST_</code>, <code>E2E_</code>, <code>Test</code>, or <code>E2E</code></li>
              <li>Model or title contains <code>Test</code>, <code>E2E</code>, or <code>Do Not Book</code></li>
              <li>Location ends with <code>Test</code></li>
              <li>Any of <code>test_data</code>, <code>is_test</code>, <code>created_by_seed</code>, <code>iter117</code> set true</li>
            </ul>
            <p className="text-green-400 text-xs mt-2">
              <CheckCircle className="w-3 h-3 inline mr-1" />
              Vehicles with active bookings are protected
            </p>
            <p className="text-green-400 text-xs">
              <CheckCircle className="w-3 h-3 inline mr-1" />
              Real listings (Ssangyong Musso, Our Kaapi Story Coffee Trailer) excluded — they don't match any test pattern
            </p>
          </div>

          {/* Active counts summary */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-amber-900/20 border border-amber-500/30 rounded p-2">
              <p className="text-amber-300 uppercase tracking-wide">Active</p>
              <p
                className="text-2xl font-bold text-amber-200"
                data-testid="active-test-vehicles-count"
              >{testVehicles?.active_count ?? testVehicles?.count ?? 0}</p>
              <p className="text-[10px] text-amber-200/60">need cleanup</p>
            </div>
            <div className="bg-slate-800/40 border border-slate-500/30 rounded p-2">
              <p className="text-slate-400 uppercase tracking-wide">Archived</p>
              <p
                className="text-2xl font-bold text-slate-300"
                data-testid="archived-test-vehicles-count"
              >{testVehicles?.archived_count ?? 0}</p>
              <p className="text-[10px] text-slate-300/60">audit-only</p>
            </div>
          </div>

          {testVehicles && (testVehicles.active_count ?? testVehicles.count ?? 0) > 0 && (
            <div className="bg-black/30 p-3 rounded-lg" data-testid="active-test-vehicles-sample">
              <p className="text-xs text-white/60 mb-2">Active test vehicles to be cleaned up (showing first 10):</p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(testVehicles.active_vehicles || testVehicles.vehicles || []).slice(0, 10).map((v, i) => (
                  <div key={i} className="text-xs text-white/80 flex justify-between gap-2">
                    <span className="truncate">
                      <span className="font-mono text-white/50">{(v.vehicle_id || '').substring(0, 18)}</span>
                      {' · '}
                      {v.make} {v.model || v.title}
                    </span>
                    <span className="text-white/40 shrink-0">{v.owner_email || v.location || ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preferred: safe archive (non-destructive, reversible) */}
          <Button
            onClick={() => openConfirmModal('archive-vehicles')}
            variant="outline"
            className="w-full border-amber-500/50 text-amber-200 hover:bg-amber-900/30"
            disabled={!testVehicles || (testVehicles.active_count ?? testVehicles.count ?? 0) === 0}
            data-testid="archive-test-vehicles-btn"
          >
            <Database className="w-4 h-4 mr-2" />
            Archive {testVehicles?.active_count ?? testVehicles?.count ?? 0} Test Vehicles (Safe)
          </Button>

          {/* Opt-in: destructive hard delete */}
          <Button
            onClick={() => openConfirmModal('delete-vehicles')}
            variant="destructive"
            className="w-full"
            disabled={!testVehicles || (testVehicles.active_count ?? testVehicles.count ?? 0) === 0}
            data-testid="delete-test-vehicles-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete {testVehicles?.active_count ?? testVehicles?.count ?? 0} Test Vehicles (Permanent)
          </Button>
        </CardContent>
      </Card>

      {/* Verification Section */}
      <Card className="bg-black/40 border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-blue-400" />
            Verification
          </CardTitle>
          <CardDescription>
            Verify cleanup was successful before launch
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={fetchVerification}
            variant="outline"
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            data-testid="verify-cleanup-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Run Verification
          </Button>

          {verification && (
            <div className="mt-4 space-y-4">
              {/* Overall Status */}
              <div className={`p-4 rounded-lg ${verification.verification_passed ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
                <div className="flex items-center gap-2">
                  {verification.verification_passed ? (
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-400" />
                  )}
                  <span className={`font-bold ${verification.verification_passed ? 'text-green-400' : 'text-red-400'}`}>
                    {verification.verification_passed ? 'ALL CHECKS PASSED' : 'SOME CHECKS FAILED'}
                  </span>
                </div>
              </div>

              {/* Individual Checks */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(verification.checks || {}).map(([key, passed]) => (
                  <div 
                    key={key}
                    className={`p-3 rounded-lg flex items-center gap-2 ${passed ? 'bg-green-500/10' : 'bg-red-500/10'}`}
                  >
                    {passed ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <span className="text-sm text-white/80">
                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                ))}
              </div>

              {/* Dashboard Totals After Cleanup */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-black/30 p-4 rounded-lg">
                  <p className="text-xs text-white/60 mb-1">Earnings (Real Only)</p>
                  <p className="text-2xl font-bold text-green-400">
                    ${verification.dashboard_totals_after_cleanup?.earnings_dollars?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="bg-black/30 p-4 rounded-lg">
                  <p className="text-xs text-white/60 mb-1">Bonds Held (Real Only)</p>
                  <p className="text-2xl font-bold text-blue-400">
                    ${verification.dashboard_totals_after_cleanup?.bonds_held_dollars?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>

              {/* Details */}
              <div className="text-xs text-white/60 space-y-1">
                <p>Remaining test users: {verification.details?.remaining_test_users || 0}</p>
                <p>Total users: {verification.details?.total_users || 0}</p>
                <p>Audit logs preserved: {verification.details?.audit_logs_count || 0}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="bg-gray-900 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {getActionTitle()}
            </DialogTitle>
            <DialogDescription className="text-white/70">
              {getActionDescription()}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-sm text-red-400 font-medium mb-2">
                This action is irreversible
              </p>
              <p className="text-xs text-white/60">
                Type <strong className="text-white">{getConfirmationText()}</strong> to confirm
              </p>
            </div>
            
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`Type ${getConfirmationText()}`}
              className="bg-black/50 border-white/20 text-white"
              data-testid="confirm-input"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
              className="border-white/20"
            >
              Cancel
            </Button>
            <Button
              onClick={executeAction}
              disabled={confirmText !== getConfirmationText() || actionLoading}
              variant="destructive"
              data-testid="confirm-action-btn"
            >
              {actionLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LaunchPreparation;
